// Client-side store for a wisdom session.
//
// State lives in localStorage so refresh restores in-progress work, and every
// UI action updates it synchronously — no network wait on the hot path.
//
// Anti-cheat events (asked, use_hint, answer_correctly, wrong_explanation) are
// queued in `pendingEvents` and flushed to POST /api/wisdoms/sync_events
// periodically, on wisdom transitions, and on pagehide (via sendBeacon).
// The sync endpoint is idempotent so retries after failure are safe.

const STORAGE_PREFIX = 'wisdom_session:'
const STATE_VERSION = 1

function storageKey(sessionId) {
  return `${STORAGE_PREFIX}${sessionId}`
}

function emptyState(sessionId) {
  return {
    version: STATE_VERSION,
    sessionId,
    wisdoms: [],
    targetScore: 0,
    completedWisdoms: [],
    masteredWisdomIds: [],
    // wisdomState[wisdomId] = { asked, hintUsed, wrongExplanation, score, answered }
    wisdomState: {},
    pendingEvents: [],
    lastSyncedAt: 0
  }
}

export function load(sessionId) {
  if (typeof window === 'undefined' || !window.localStorage) return null
  try {
    const raw = window.localStorage.getItem(storageKey(sessionId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed.version !== STATE_VERSION) return null
    return parsed
  } catch (e) {
    return null
  }
}

export function save(sessionId, state) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.setItem(storageKey(sessionId), JSON.stringify(state))
  } catch (e) {
    // Quota / private mode — ignore, the session will still run in-memory.
  }
}

export function clear(sessionId) {
  if (typeof window === 'undefined' || !window.localStorage) return
  try {
    window.localStorage.removeItem(storageKey(sessionId))
  } catch (e) {
    // ignore
  }
}

// Initialize state from a server /random response.
export function hydrateFromServer(sessionId, { wisdoms, progress }) {
  const state = emptyState(sessionId)
  state.wisdoms = wisdoms || []
  state.targetScore = wisdoms?.length || 0

  const completed = progress?.completed_wisdoms || []
  const mastered = progress?.mastered_wisdom_ids || []

  state.completedWisdoms = completed.map((c) => ({
    wisdom_id: c.wisdom_id,
    score: c.score,
    hintsUsed: c.score === 1 ? 0 : 1,
    mastered: c.mastered
  }))
  state.masteredWisdomIds = [...mastered]

  completed.forEach((c) => {
    state.wisdomState[c.wisdom_id] = {
      asked: true,
      hintUsed: c.score !== 1,
      wrongExplanation: false,
      score: c.score,
      answered: true
    }
  })

  return state
}

function ensureWisdom(state, wisdomId) {
  if (!state.wisdomState[wisdomId]) {
    state.wisdomState[wisdomId] = {
      asked: false,
      hintUsed: false,
      wrongExplanation: false,
      score: 0,
      answered: false
    }
  }
  return state.wisdomState[wisdomId]
}

// Push an event. Returns the new state (immutably updated at the top level so
// React re-renders when it's stored back).
export function pushEvent(state, event) {
  const next = { ...state, wisdomState: { ...state.wisdomState }, pendingEvents: [...state.pendingEvents] }
  const w = { ...ensureWisdom(next, event.wisdom_id) }
  next.wisdomState[event.wisdom_id] = w

  switch (event.event) {
    case 'asked':
      // Client-side dedup: only reset if we're starting a fresh attempt on an
      // already-answered wisdom (kid cycled back after failing to master).
      if (w.answered) {
        w.asked = true
        w.hintUsed = false
        w.wrongExplanation = false
        w.answered = false
        w.score = 0
        next.pendingEvents.push({ wisdom_id: event.wisdom_id, event: 'asked' })
      } else if (!w.asked) {
        w.asked = true
        next.pendingEvents.push({ wisdom_id: event.wisdom_id, event: 'asked' })
      }
      break
    case 'use_hint':
      if (!w.hintUsed) {
        w.hintUsed = true
        next.pendingEvents.push({ wisdom_id: event.wisdom_id, event: 'use_hint' })
      }
      break
    case 'answer_correctly': {
      const score = w.hintUsed ? 0 : 1
      w.score = score
      w.answered = true
      next.pendingEvents.push({ wisdom_id: event.wisdom_id, event: 'answer_correctly' })
      break
    }
    case 'wrong_explanation':
      w.score = 0
      w.wrongExplanation = true
      w.answered = true
      next.pendingEvents.push({ wisdom_id: event.wisdom_id, event: 'wrong_explanation' })
      break
    default:
      // ignore unknown events
      break
  }

  return next
}

// Total score derived from wisdom state (mirrors server logic).
export function totalScore(state) {
  return Object.values(state.wisdomState).reduce((sum, w) => sum + (w.score || 0), 0)
}

export function masteredWisdomIds(state) {
  return Object.entries(state.wisdomState)
    .filter(([, w]) => w.score === 1)
    .map(([id]) => Number(id))
}

export function completedWisdoms(state) {
  return Object.entries(state.wisdomState)
    .filter(([, w]) => w.answered)
    .map(([id, w]) => ({
      wisdom_id: Number(id),
      score: w.score,
      hintsUsed: w.hintUsed ? 1 : 0,
      mastered: w.score === 1
    }))
}

// Fetch initial wisdoms + progress from server. Only called when localStorage
// is empty (first visit or cross-device resume).
export async function fetchRandom(sessionId) {
  const response = await fetch(`/api/wisdoms/random?session_id=${sessionId}`)
  if (!response.ok) {
    throw new Error(`Failed to load wisdoms: ${response.status}`)
  }
  return response.json()
}

// POST queued events to the server. Returns { ok, applyAck } — on success,
// callers should run applyAck via a functional setState so events pushed
// during the flush aren't overwritten.
export async function flush(sessionId, state) {
  if (!state.pendingEvents.length) return { ok: true, applyAck: (s) => s }

  const inFlightCount = state.pendingEvents.length
  const inFlight = state.pendingEvents.slice(0, inFlightCount)

  try {
    const response = await fetch(`/api/wisdoms/sync_events?session_id=${sessionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ events: inFlight })
    })
    if (!response.ok) {
      return { ok: false, applyAck: (s) => s }
    }
    return {
      ok: true,
      applyAck: (current) => ({
        ...current,
        pendingEvents: current.pendingEvents.slice(inFlightCount),
        lastSyncedAt: Date.now()
      })
    }
  } catch (e) {
    return { ok: false, applyAck: (s) => s }
  }
}

// Fire-and-forget flush for pagehide. `sendBeacon` doesn't return a response,
// so we optimistically drop the queue — worst case a small batch is lost.
export function flushBeacon(sessionId, state) {
  if (!state.pendingEvents.length) return state
  if (typeof navigator === 'undefined' || !navigator.sendBeacon) return state

  const blob = new Blob(
    [JSON.stringify({ events: state.pendingEvents })],
    { type: 'application/json' }
  )
  const sent = navigator.sendBeacon(`/api/wisdoms/sync_events?session_id=${sessionId}`, blob)
  if (!sent) return state
  return { ...state, pendingEvents: [], lastSyncedAt: Date.now() }
}
